import { useTranslation } from "react-i18next";
import { formatFall, stepPath, type ChartFrame, type DatedValue } from "../portfolio-tracker";

const FALL_HEIGHT = 76;
const FALL_TOP = 4;
const FALL_BOTTOM_GAP = 6;

/** How far below its last high the portfolio was, on the same days as the chart above. */
export function FallStrip({
  frame,
  fall,
  percent,
  hoverDay,
}: {
  frame: ChartFrame;
  fall: DatedValue[];
  percent: number;
  hoverDay: string | null;
}) {
  const { t } = useTranslation("portfolioTracker");
  const bottom = FALL_HEIGHT - FALL_BOTTOM_GAP;
  const deepest = Math.min(-1, ...fall.map((point) => point.value));
  const y = (value: number) => FALL_TOP + (value / deepest) * (bottom - FALL_TOP);
  const line = stepPath(fall.map((point) => ({ x: frame.x(point.date), y: y(point.value) })));
  const area = `${line}V${y(0).toFixed(1)}H${frame.x(fall[0].date).toFixed(1)}Z`;

  return (
    <div className="tracker-fall">
      <svg
        viewBox={`0 0 ${frame.width} ${FALL_HEIGHT}`}
        role="img"
        aria-label={t("chart.labelFall", { fall: formatFall(percent) })}
      >
        <line className="tracker-chart-grid" x1={frame.left} x2={frame.right} y1={y(0)} y2={y(0)} />
        <text className="tracker-chart-tick" x={frame.left - 8} y={y(0) + 3.5} textAnchor="end">
          0%
        </text>
        <text className="tracker-chart-tick" x={frame.left - 8} y={y(deepest) + 3.5} textAnchor="end">
          {formatFall(-deepest)}
        </text>
        <path className="tracker-fall-area" d={area} />
        <path className="tracker-fall-line" d={line} />
        {hoverDay && (
          <line
            className="tracker-chart-cross"
            x1={frame.x(hoverDay)}
            x2={frame.x(hoverDay)}
            y1={FALL_TOP}
            y2={bottom}
          />
        )}
      </svg>
    </div>
  );
}
